import { useState, useMemo, useEffect, Fragment, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, ButtonPrimary, H4Bold, LabelRegular } from '@comeon/ui';
import { selectBalance, selectLocale } from '~/components/account/ducks/index.js';
import useTranslations from '../../../translation/use-translations';
import {
	FETCH_GAME_ROOM,
	fetchLiveStreamData,
	fetchProfile,
	playerBalanceInEventCurrency,
	setPlayerBalanceInEventCurrency
} from '../ducks/we-spin-duck.js';

import { PENDING_START, RUNNING, ENDED, LIVE_STATES } from '../constants/we-spin-stream-states.js';
import {
	WE_SPIN_PREVIEW_ROOM_TEXT_KEYS,
	WE_SPIN_BUY_IN_TEXT_KEYS,
	WE_SPIN_GAME_ROOM_FOOTER_TEXT_KEYS,
	WE_SPIN_GAME_ROOM_TAB_ITEM_BALANCE,
	WE_SPIN_GAME_ROOM_TAB_ITEM_CHAT
} from '../constants/we-spin-text-keys.js';
import { FREE_WESPIN, LABEL_MAPPING } from '../constants/we-spin-boost-constants.js';
import { fetchCurrentEvent, selectCurrentEvent, setCurrentEventRef } from '../ducks/we-spin-home-game-duck.js';
import {
	eventTagDescriptionText,
	eventTagTitleText,
	WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS
} from '../constants/we-spin-event-tags';
import {
	WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	WE_SPIN_DASHBOARD_SCHEDULED_TEXT_KEYS,
	WE_SPIN_DASHBOARD_TAB_ITEM_ADD_STREAM
} from '../constants/we-spin-dashboard-text-keys';
import Loading from '~/components/misc/loading';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from '~/components/sidebar';
import BuyinRefundModal from '../components/modals/buyin-refund-modal';
import AdjustBuyinModal from '../components/modals/adjust-buyin-modal';
import StreamElement from './stream-element';
import generateSrcSet from '~/utils/generateSrcSet';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper';
import { isBiggerSelector } from '~/components/layout/mediaquery';
import Icon, { ICON_RIGHT } from '~/components/icon';
import WE_SPIN_GAME_ROOM_TAB_ITEMS from './we-spin-game-room-tab-items';
import TabLabel from '../components/tab-label';
import WeSpinGameRoomChat from './we-spin-game-room-chat';
import ReturnBalance from '../components/return-balance';
import currencyFormatter from '~/libs/currency-formatter';
import WeSpinTable from '../components/we-spin-table';
import { selectEventByRef } from '../ducks/we-spin-dashboard-duck';
import { Collapse, CollapseGroup } from './collapse';
import {
	ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS,
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW
} from '../constants/we-spin-route-constants';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import SidebarScrollAware from '~/utils/sidebar-scroll-aware';
import { LeftSideIconType } from '~/components/sidebar/sidebar-header/sidebar-header';
import { ROUTE_WE_SPIN } from '~/components/router/constants';
import useIntervalAction from '~/hooks/use-interval-action';
import { selectConfiguration, selectGameRoom } from '../ducks/we-spin-selectors';
import WeSpinDashBoardTabBar from '../components/tab-bar/we-spin-dashboard-tab-bar';

const SIDEBAR_TITLE_SCROLL_THRESHOLD = 30;

const BoxWithDisabledBackgroundColor = styled(Box)`
	background-color: var(--link-list, rgba(var(--base-10), 1));
`;

const RefundButton = styled(Button)`
	background-color: var(--sidebar-bg, rgba(var(--base-11), 1));
	border-radius: 3px;
`;

const TEXT_KEYS = [
	...WE_SPIN_PREVIEW_ROOM_TEXT_KEYS,
	...WE_SPIN_BUY_IN_TEXT_KEYS,
	...WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS,
	...WE_SPIN_DASHBOARD_SCHEDULED_TEXT_KEYS,
	...WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	...WE_SPIN_GAME_ROOM_FOOTER_TEXT_KEYS,
	WE_SPIN_DASHBOARD_TAB_ITEM_ADD_STREAM
];

const StreamElementWrapper = styled.div`
	padding: 12px;

	iframe,
	img {
		border-radius: 12px;
		overflow: hidden;
	}
`;

const WeSpinGameRoomPreview = () => {
	const params = useParams();
	const dispatch = useDispatch();
	const router = useRouter();
	const transformPath = useContextualPathTransformer();

	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	// Common Data
	const streamView = useSelector(selectCurrentEvent);
	const authenticated = useSelector(authenticationSelector);
	const walletBalanceInPlayersCurrency = useSelector(selectBalance) || 0;
	const isOnline = LIVE_STATES.includes(streamView?.state);
	const locale = useSelector(selectLocale);
	const ownsThisEvent = !!useSelector(selectEventByRef(params.streamRef));
	const hasEventTag = streamView?.eventTags?.length > 0;
	const { weSpinViewLiveStreamDataPollInterval } = useSelector(selectConfiguration);
	const liveStreamData = useSelector(selectGameRoom(params.streamRef));
	const { wespinChatDisabled } = useSelector(selectConfiguration);
	const bonusName = useMemo(
		() =>
			eventTagTitleText(streamView?.eventTags[0], translations)
				?.toLowerCase()
				.split(' ')
				.map(item => item.charAt(0).toUpperCase() + item.slice(1))
				.join(' '),
		[streamView?.eventTags, translations]
	);

	// Init Data
	useEffect(() => {
		dispatch(fetchProfile());
		dispatch(setCurrentEventRef(params.streamRef));
		dispatch(fetchCurrentEvent());
	}, [dispatch, params.streamRef]);
	useEffect(() => {
		if (params.streamRef && authenticated) {
			dispatch(playerBalanceInEventCurrency(params.streamRef));
		} else {
			setPlayerBalanceInEventCurrency(null, params.streamRef);
		}
		return () => {
			dispatch(setPlayerBalanceInEventCurrency(null, params.streamRef));
		};
	}, [dispatch, walletBalanceInPlayersCurrency, params.streamRef, authenticated]);

	// Modals
	const [showRefundConfirmation, setShowRefundConfirmation] = useState(false);
	const [adjustAmountQuestion, setShowEditAmountQuestion] = useState(false);
	const isFreeWeSpin = streamView?.boost?.type === FREE_WESPIN;

	// StreamElement Data
	const isBigger = useSelector(isBiggerSelector);
	const streamElementData = useMemo(() => {
		const topText = streamView?.name || streamView?.games?.[0]?.gameName;
		const headerText = getWeekDayDateAndMonth(streamView?.start, translations);
		const descriptionText = hasEventTag
			? eventTagDescriptionText(streamView?.eventTags[0], translations)
			: streamView?.description;
		const currentGame = streamView?.currentGame?.gameCode ? streamView?.currentGame : null;
		const gameImageUrl = currentGame?.gameImageUrl;
		const backgroundImage = streamView?.streamBgUrl || streamView?.streamerImgUrl || gameImageUrl || null;
		const backgroundImageSrcSet = backgroundImage ? generateSrcSet(backgroundImage, true) : null;

		return { topText, headerText, descriptionText, hasEventTag, backgroundImage, backgroundImageSrcSet };
	}, [
		hasEventTag,
		streamView?.currentGame,
		streamView?.description,
		streamView?.eventTags,
		streamView?.games,
		streamView?.name,
		streamView?.start,
		streamView?.streamBgUrl,
		streamView?.streamerImgUrl,
		translations
	]);

	// Tabs
	const [activeTab, setActiveTab] = useState(WE_SPIN_GAME_ROOM_TAB_ITEM_BALANCE);

	const FILTERED_TAB_ITEMS = useMemo(
		() =>
			WE_SPIN_GAME_ROOM_TAB_ITEMS.filter(item =>
				wespinChatDisabled ? item.labelKey !== WE_SPIN_GAME_ROOM_TAB_ITEM_CHAT : true
			),
		[wespinChatDisabled]
	);

	// Actions
	const onTabChange = useCallback(e => {
		setActiveTab(e.target.value);
	}, []);
	const onLeftSideButtonClick = useCallback(() => {
		router.push(ROUTE_WE_SPIN);
	}, [router]);

	// Pool data
	useIntervalAction(
		`${FETCH_GAME_ROOM}/${params.streamRef}`,
		fetchLiveStreamData(params.streamRef),
		weSpinViewLiveStreamDataPollInterval || 5000
	);

	if (isLoadingTranslations || !streamView) return <Loading />;

	return (
		<Sidebar>
			<SidebarScrollAware>
				{scrollYPosition => (
					<SidebarHeader
						transparent={scrollYPosition < SIDEBAR_TITLE_SCROLL_THRESHOLD}
						className="transparent_title"
						leftSideIcon={LeftSideIconType.CLOSE_ICON}
						onLeftSideButtonClick={onLeftSideButtonClick}
					>
						<H4Bold className="page-head__title">Event</H4Bold>
						<div className="page-head__right-side" />
					</SidebarHeader>
				)}
			</SidebarScrollAware>
			<SidebarContent>
				{/* Modals */}
				<BuyinRefundModal
					openBuyinRefund={showRefundConfirmation}
					closeBuyinRefund={() => {
						setShowRefundConfirmation(false);
					}}
					streamRef={params.streamRef}
					isFreeWespin={isFreeWeSpin}
				/>
				<AdjustBuyinModal
					openAdjustBuyin={adjustAmountQuestion}
					closeAdjustBuyin={() => {
						setShowEditAmountQuestion(false);
					}}
					streamRef={params.streamRef}
				/>
				{/* /Modals */}

				<div className="u-pb2">
					{ownsThisEvent ? (
						<WeSpinDashBoardTabBar />
					) : (
						<div className="user-options u-mb1">
							<div className="filters">
								<fieldset className="filters__inner-wrapper we-spin-navigation-tabs u-flex u-relative">
									{FILTERED_TAB_ITEMS.length > 0 &&
										FILTERED_TAB_ITEMS.map(tabItem => (
											<Fragment key={tabItem.link}>
												<input
													type="radio"
													id={`f-${tabItem.labelKey}`}
													name="f-dsh-nav-tabs"
													data-tag={tabItem.labelKey}
													checked={tabItem.labelKey === activeTab}
													value={tabItem.labelKey}
													onChange={onTabChange}
													className={tabItem.labelKey === activeTab ? 'active' : ''}
												/>
												<TabLabel
													htmlFor={`f-${tabItem.labelKey}`}
													data-at={tabItem.dataAt}
													className="filters__filter u-text-c u-cursor-hand u-flex u-fxb-center-v-h"
												>
													{translations[tabItem.labelKey]}
												</TabLabel>
											</Fragment>
										))}
									<div className="sliding-border u-absolute u-pos-b0" />
								</fieldset>
							</div>
						</div>
					)}
					{((isBigger && activeTab === WE_SPIN_GAME_ROOM_TAB_ITEM_BALANCE) || !isBigger) && (
						<StreamElementWrapper className="top-of-page-image-wrapper u-relative">
							<StreamElement
								showBoost
								compact
								hideText={activeTab === WE_SPIN_GAME_ROOM_TAB_ITEM_CHAT}
								size="small"
								type={isOnline && !isBigger ? 'STREAM' : 'IMAGE'}
								streamLinkUrl={
									isOnline && !isBigger ? streamView.streamUrl : streamElementData.backgroundImage
								}
								topText={streamElementData.topText}
								headerText={streamElementData.headerText}
								streamerDescription={streamElementData.descriptionText}
								boost={streamView.boost}
								srcSet={streamElementData.backgroundImageSrcSet}
								streamView={streamView}
								showEventTagBg={streamElementData.hasEventTag}
							/>
						</StreamElementWrapper>
					)}
					{activeTab === WE_SPIN_GAME_ROOM_TAB_ITEM_BALANCE && liveStreamData && (
						<div className="u-mr1 u-ml1 u-mb2">
							<ReturnBalance balance={liveStreamData.yourBalance} />
						</div>
					)}
					{!ownsThisEvent && streamView.joined && activeTab === WE_SPIN_GAME_ROOM_TAB_ITEM_BALANCE && (
						<div className="u-mr1 u-ml1">
							<WeSpinTable className="transactions-details-table">
								<tbody>
									<tr>
										<td>
											<span>Total Buy-ins</span>
										</td>
										<td className="u-text-r">
											<span>
												{liveStreamData
													? currencyFormatter.format(
															liveStreamData.sessionBalance,
															streamView.currencyCode,
															locale
														)
													: ''}
											</span>
										</td>
									</tr>
									<tr>
										<td>
											<span>My Buy-In Boost</span>
										</td>
										<td className="u-text-r">
											<span>
												{liveStreamData
													? currencyFormatter.format(
															liveStreamData.yourBoost,
															streamView.currencyCode,
															locale
														)
													: ''}
											</span>
										</td>
									</tr>
									<tr>
										<td>
											<span>My share of buy-ins</span>
										</td>
										<td className="u-text-r">
											<span>
												{liveStreamData && `${(liveStreamData.yourShare * 100).toFixed(0)} %`}
											</span>
										</td>
									</tr>
									<tr>
										<td>
											<span>Number of participants</span>
										</td>
										<td className="u-text-r">
											<span>{streamView.noParticipants}</span>
										</td>
									</tr>
									<tr>
										<td>
											<span>Net result for event</span>
										</td>
										<td className="u-text-r">
											<span>
												{liveStreamData
													? currencyFormatter.format(
															liveStreamData.yourBalance - liveStreamData.yourBuyIn,
															streamView.currencyCode,
															locale
														)
													: ''}
											</span>
										</td>
									</tr>
								</tbody>
							</WeSpinTable>
						</div>
					)}

					{ownsThisEvent && activeTab !== WE_SPIN_GAME_ROOM_TAB_ITEM_CHAT && (
						<>
							<div className="u-pl1 u-pr1">
								<WeSpinTable className="transactions-details-table u-mt2">
									<tbody>
										<tr>
											<td>
												<span>My Buy-In</span>
											</td>
											<td className="u-text-r">
												<span>
													{liveStreamData
														? currencyFormatter.format(
																liveStreamData.yourBuyIn,
																streamView.currencyCode,
																locale
															)
														: ''}
												</span>
											</td>
										</tr>
										{hasEventTag && (
											<tr>
												<td>
													<span>Event Type</span>
												</td>
												<td className="u-text-r">
													<span>{bonusName}</span>
												</td>
											</tr>
										)}
										{streamView.boost && (
											<>
												<tr>
													<td>
														<span>Boost Type</span>
													</td>
													<td className="u-text-r">
														<span>{LABEL_MAPPING[streamView.boost.type]}</span>
													</td>
												</tr>
												<tr>
													<td>
														<span>Boost Amount</span>
													</td>
													<td className="u-text-r">
														<span>
															{currencyFormatter.format(
																streamView.boost.amount,
																streamView.boost.currency,
																locale
															)}
														</span>
													</td>
												</tr>
											</>
										)}
									</tbody>
								</WeSpinTable>
							</div>
							<CollapseGroup className="u-mt1 u-mb1 u-pl1 u-pr1">
								<Collapse
									type="link"
									label={translations.WE_SPIN_BUY_IN_PLAYERS_LABEL}
									dataAt="collapse-button-games-excluded-bonus"
									link={transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS}/${params.streamRef}`)}
									returnLink={`${ROUTE_WE_SPIN_GAME_ROOM_PREVIEW}/${params.streamRef}`}
									value={streamView.noParticipants}
									className="we-spin-buyin__collapse"
									icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />}
								/>

								{/* <Collapse */}
								{/*	type="link" */}
								{/*	label={translations.WE_SPIN_PREVIEW_ROOM_RAFFLE_LABEL} */}
								{/*	dataAt="collapse-button-games-excluded-bonus" */}
								{/*	link={`${transformPath(ROUTE_WE_SPIN_GAME_ROOM_LIST_RAFFLE)}/${params.streamRef}`} */}
								{/*	value={myRaffleTicketCount || 0} */}
								{/*	className="we-spin-buyin__collapse" */}
								{/*	icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />} */}
								{/* /> */}
							</CollapseGroup>
						</>
					)}
				</div>
				{!wespinChatDisabled && activeTab === WE_SPIN_GAME_ROOM_TAB_ITEM_CHAT && (
					<WeSpinGameRoomChat streamRef={params.streamRef} />
				)}
			</SidebarContent>
			<SidebarFooter>
				{streamView.joined &&
					streamView.state !== PENDING_START &&
					streamView.state !== RUNNING &&
					streamView.state !== ENDED && (
						<BoxWithDisabledBackgroundColor
							flex="1"
							mb="0"
							p="5"
							display="flex"
							flexDirection="column"
							alignItems="center"
							className="wespin-buyin-button-large"
						>
							{/* <Caption pb="5">{tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_DEPOSIT_REQUIRED}</Caption> */}
							<ButtonPrimary
								onClick={() => {
									setShowEditAmountQuestion(true);
								}}
								disabled={false}
								className="u-mb1"
							>
								{translations.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT}
							</ButtonPrimary>
							<RefundButton
								onClick={() => {
									setShowRefundConfirmation(true);
								}}
								disabled={false}
							>
								<LabelRegular>{translations.WE_SPIN_PREVIEW_ROOM_REFUND_BUTTON}</LabelRegular>
							</RefundButton>
						</BoxWithDisabledBackgroundColor>
					)}
			</SidebarFooter>
		</Sidebar>
	);
};

export default WeSpinGameRoomPreview;
